'use client';

import { getClientApplicationConfig } from '@hc-frontend/core-context';
import { overrideExperimentAdapterFactory } from '@hc-frontend/features-experimentation';
import { useCompleteTrackingContext } from '@hc-frontend/features-tracking';
import { trackExperimentView } from '@hc-frontend/third-party-snowplow';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

const BuilderExperimentTrackingSchema = z.object({
  experimentName: z.string(),
  experimentVariant: z.string(),
  experimentSplit: z.union([z.literal('builder'), z.literal('marketing-tool')]),
});

type BuilderExperimentTrackingProps = Partial<
  z.infer<typeof BuilderExperimentTrackingSchema>
>;

export function BuilderExperimentTracking({
  experimentName,
  experimentVariant,
  experimentSplit,
}: BuilderExperimentTrackingProps) {
  const { tracker } = useCompleteTrackingContext();
  const config = getClientApplicationConfig();
  const domainUserId = tracker.getDomainUserId();

  const overrideExperiment = useMemo(
    () =>
      overrideExperimentAdapterFactory({
        amplitudeApiKey: config.amplitude.identify.apiKey,
        amplitudeIdentifyUrl: config.amplitude.identify.baseUrl,
      }),
    [config.amplitude.identify.apiKey, config.amplitude.identify.baseUrl],
  );

  useEffect(() => {
    const validated = BuilderExperimentTrackingSchema.safeParse({
      experimentName,
      experimentVariant,
      experimentSplit,
    });

    if (!validated.success) {
      return;
    }

    trackExperimentView({
      experimentName: validated.data.experimentName,
      variantName: validated.data.experimentVariant,
      splitSource: validated.data.experimentSplit,
    });

    if (!domainUserId) {
      return;
    }

    overrideExperiment({
      domainUserId: domainUserId,
      experimentName: validated.data.experimentName,
      experimentVariant: validated.data.experimentVariant,
    });
  }, [
    experimentName,
    experimentSplit,
    experimentVariant,
    domainUserId,
    overrideExperiment,
  ]);

  return null;
}
